// src/components/audio/FlaggedRowDispositionToggle.js
//
// Three-state mini toggle that lives in the ACTIONS column of flagged-rows
// tables. Compact sibling of DispositionPill.js (which is the wider AudioAudit
// version). Same vocabulary, same persistence — just sized for table cells.
//
// SHIP 3 BEHAVIORAL CONTRACT
// ==========================
// - Disposition is keyed by (submission_id, audio_column) — same as DispositionPill.
// - Click an inactive button → set that disposition.
// - Click the ACTIVE button → clear (return to unset).
// - Saves immediately to the audio_notes ConfigurationCRUD doc via DataService.
// - Optimistic update: local state flips first, server save runs in background;
//   on failure, we roll back and surface a tiny inline error.

import React, { useState } from 'react';
import { Check, AlertCircle, X } from 'lucide-react';
import { setDisposition, getDispositionFor, DISPOSITIONS } from './audioNotes';
import { DataService } from '../../services/dataService';

const ICONS = {
  good:    Check,
  concern: AlertCircle,
  reject:  X,
};

const FlaggedRowDispositionToggle = ({
  submissionId,
  audioColumn,
  audioNotesDoc,
  onAudioNotesUpdated,
  projectId,
  isDarkMode,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  if (!submissionId || !audioColumn || !projectId) return null;

  const current = getDispositionFor(audioNotesDoc, submissionId, audioColumn);

  const colors = isDarkMode
    ? { fg: '#9CA3AF', bg: 'rgba(255,255,255,0.03)', bd: 'rgba(255,255,255,0.08)',
        bgHover: 'rgba(255,255,255,0.06)' }
    : { fg: '#6B7280', bg: 'rgba(0,0,0,0.03)',       bd: 'rgba(0,0,0,0.08)',
        bgHover: 'rgba(0,0,0,0.05)' };

  const handleClick = async (e, dispId) => {
    e.stopPropagation();
    if (saving) return;

    // Toggle: if clicking the active disposition, clear it
    const next = (dispId === current) ? null : dispId;

    // Optimistic: update local immediately
    const optimistic = setDisposition(audioNotesDoc || { notes: [], dispositions: {} },
                                       submissionId, audioColumn, next);
    if (onAudioNotesUpdated) onAudioNotesUpdated(optimistic);
    setError(null);
    setSaving(true);

    try {
      await DataService.saveAudioNotes(projectId, optimistic);
    } catch (err) {
      console.error('[FlaggedRowDispositionToggle] save failed:', err);
      // Roll back
      if (onAudioNotesUpdated) onAudioNotesUpdated(audioNotesDoc);
      setError(err?.message || 'Save failed');
      setTimeout(() => setError(null), 3500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <span
      title={error ? `Save failed: ${error}` : undefined}
      style={{
        display: 'inline-flex', flexShrink: 0,
        gap: '2px',
        padding: '2px',
        background: colors.bg,
        border: `1px solid ${error ? '#DC262655' : colors.bd}`,
        borderRadius: '999px',
        opacity: saving ? 0.6 : 1,
      }}
    >
      {DISPOSITIONS.map((d) => {
        const Icon = ICONS[d.id];
        const active = current === d.id;
        return (
          <button
            key={d.id}
            onClick={(e) => handleClick(e, d.id)}
            disabled={saving}
            title={`${d.label}${active ? ' (click to clear)' : ` — ${d.description}`}`}
            style={{
              width: '20px', height: '20px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: 0,
              background: active ? `${d.color}22` : 'transparent',
              border: 'none',
              borderRadius: '999px',
              color: active ? d.color : colors.fg,
              cursor: saving ? 'wait' : 'pointer',
              transition: 'background 0.12s ease, color 0.12s ease',
            }}
            onMouseEnter={(e) => {
              if (!active && !saving) {
                e.currentTarget.style.background = colors.bgHover;
                e.currentTarget.style.color = d.color;
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.fg;
              }
            }}
          >
            <Icon size={11} strokeWidth={2.5} />
          </button>
        );
      })}
    </span>
  );
};

export default FlaggedRowDispositionToggle;
